import { DatePicker, Space } from 'antd';
import { ClipLoader } from 'react-spinners';
import {
  Export,
  MagnifyingGlass,
  SlidersHorizontal,
  X,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';

const { RangePicker } = DatePicker;

interface TransactionControlsProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  startDate: string;
  endDate: string;
  handleDateChange: (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    dateStrings: [string, string]
  ) => void;
  disabledDate: (current: dayjs.Dayjs) => boolean;
  handleModalOpen: () => void;
  handleDownload: () => void;
  loadingDownload: boolean;
  setOffset: (offset: number) => void;
  isPreActive: boolean;
}

function TransactionControls({
  searchQuery,
  setSearchQuery,
  startDate,
  endDate,
  handleDateChange,
  disabledDate,
  handleModalOpen,
  handleDownload,
  loadingDownload,
  setOffset,
  isPreActive,
}: TransactionControlsProps) {
  const { screenView } = useScreenView();
  const renderSearchBar = () => (
    <div
      className={`flex ${screenView === 'mobile' ? 'w-full' : 'w-1/2'} h-[48px] max-h-[48px] min-w-[180px] flex-grow items-center gap-1 rounded-full bg-white px-4 py-3 text-[#7D8191]`}
    >
      <MagnifyingGlass size={20} />
      <input
        type="text"
        placeholder="Search"
        className="ml-2 flex-grow bg-transparent text-gray-600 outline-none disabled:cursor-not-allowed"
        value={searchQuery}
        onChange={(e) => {
          setOffset(0);
          setSearchQuery(e.target.value);
        }}
        disabled={isPreActive}
      />
      {searchQuery && searchQuery?.length > 0 && (
        <button
          className="rounded-full p-[2px] hover:bg-gray-200"
          onClick={() => setSearchQuery('')}
        >
          <X size={20} />
        </button>
      )}
    </div>
  );

  const renderControls = () => (
    <div
      className={`flex w-full flex-row sm:justify-start sm:space-x-2 md:justify-end md:space-x-1 lg:space-x-3`}
    >
      <Space
        className={`h-[48px] ${screenView === 'mobile' ? 'w-[220px]' : 'min-w-[220px]'} overflow-visible`}
        direction="vertical"
        size={12}
      >
        <RangePicker
          onChange={handleDateChange}
          disabledDate={disabledDate}
          className="h-full min-h-[48px] overflow-hidden"
          value={[
            startDate ? dayjs(startDate) : null,
            endDate ? dayjs(endDate) : null,
          ]}
          disabled={isPreActive}
          popupClassName={
            screenView === 'mobile'
              ? '!max-w-[95vw] !overflow-x-scroll [&_.ant-picker-panels]:!flex [&_.ant-picker-panels]:!overflow-x-scroll [&_.ant-picker-panel-container]:!flex [&_.ant-picker-panel-container]:!overflow-visible disabled:cursor-not-allowed'
              : undefined
          }
        />
      </Space>

      <button
        className="flex h-12 w-12 items-center justify-center rounded-full border-[1px] border-[#DADADA] text-[#7D8191] hover:bg-gray-200"
        onClick={handleModalOpen}
      >
        <SlidersHorizontal size={20} />
      </button>

      <button
        onClick={handleDownload}
        className="flex h-12 w-12 items-center justify-center rounded-full border-[1px] border-[#DADADA] text-[#7D8191] hover:bg-gray-200"
      >
        <Export size={20} />
      </button>
      {loadingDownload && (
        <div className="flex items-center justify-center">
          <ClipLoader
            color="#1D213B"
            loading={loadingDownload}
            size={20}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="flex h-full w-full items-center justify-center"
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`justify-between! flex w-full items-center align-middle sm:flex-col sm:gap-4 md:h-[48px] md:flex-row md:gap-1 lg:gap-4`}
    >
      {renderSearchBar()}
      {renderControls()}
    </div>
  );
}

export default TransactionControls;
